import { Edit, Lock } from '@mui/icons-material';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    TableProps,
    TextField,
    Tooltip,
} from '@mui/material';
import axios, { AxiosError } from 'axios';
import { useContext, useState, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import constants from '../config/constants';
import { AlertContext } from '../context/alert.context';
import { useLanguage } from '../context/language.context';
import { IPrivateService } from '../types';
import { getLocalizedErrorMessage } from '../utils/functions';

const DESCRIPTION_LIMIT = 400;
const TITLE_LIMIT = 80;
type FormData = {
    title: {
        he: string;
        en: string;
    };
    description: {
        he: string;
        en: string;
    };
    duration: number;
    price: number;
    price_before_discount?: number;
    gap_duration?: number;
    type?: string;
    is_series?: boolean;
    series_size?: number;
};
const PrivateServiceItem = (props: {
    service: IPrivateService;
    refetch: () => void;
    seriesTermsConfirmed?: boolean;
    onSeriesTermsAccepted?: () => void;
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false);
    const [pendingSubmitData, setPendingSubmitData] = useState<FormData | null>(null);
    const alert = useContext(AlertContext);
    const { t, dir } = useLanguage();

    const form = useForm<FormData>({
        defaultValues: {
            description: {
                he: props.service.description?.he || '',
                en: props.service.description?.en || '',
            },
            duration: props.service.duration,
            gap_duration: props.service.gap_duration,
            price: props.service.price,
            price_before_discount: props.service.price_before_discount,
            title: {
                en: props.service.title?.en || '',
                he: props.service.title?.he || '',
            },
            type: props.service.type,
            is_series: props.service.type === '1TO1_SERIES',
            series_size: props.service.series_size ?? 4,
        },
    });

    useEffect(() => {
        if (isDialogOpen) {
            form.reset({
                description: {
                    he: props.service.description?.he || '',
                    en: props.service.description?.en || '',
                },
                duration: props.service.duration,
                gap_duration: props.service.gap_duration,
                price: props.service.price,
                price_before_discount: props.service.price_before_discount,
                title: {
                    en: props.service.title?.en || '',
                    he: props.service.title?.he || '',
                },
                type: props.service.type,
                is_series: props.service.type === '1TO1_SERIES',
                series_size: props.service.series_size ?? 4,
            });
        }
    }, [isDialogOpen, props.service, form]);

    const handleSave: SubmitHandler<FormData> = async (data) => {
        // Series terms gate: first time this expert turns a service into a series
        // we must show the disclaimer modal before submitting.
        const isConvertingToSeries =
            constants.ENABLE_SERIES_EDITING &&
            data.is_series === true &&
            props.service.type !== '1TO1_SERIES';
        if (isConvertingToSeries && !props.seriesTermsConfirmed) {
            setPendingSubmitData(data);
            setIsTermsDialogOpen(true);
            return;
        }
        await submitSave(data, false);
    };

    const submitSave = async (data: FormData, acceptSeriesTerms: boolean) => {
        try {
            const isSeriesEditable =
                constants.ENABLE_SERIES_EDITING &&
                (props.service.type === '1TO1' || props.service.type === '1TO1_SERIES');
            const payload: any = {
                _id: props.service._id,
                title: {
                    he: data.title?.he ?? props.service.title?.he,
                    en: data.title?.en ?? props.service.title?.en,
                },
                description: {
                    he: data.description?.he ?? props.service.description?.he,
                    en: data.description?.en ?? props.service.description?.en,
                },
                gap_duration: data.gap_duration ?? props.service.gap_duration,
                duration: data.duration ?? props.service.duration,
                price: data.price ?? props.service.price,
                price_before_discount: data.price_before_discount ?? props.service.price_before_discount,
            };
            if (isSeriesEditable) {
                if (data.is_series) {
                    payload.type = '1TO1_SERIES';
                    payload.series_size = Number(data.series_size) || 2;
                } else {
                    payload.type = '1TO1';
                    payload.series_size = null;
                }
            }
            if (acceptSeriesTerms) payload.acceptSeriesTerms = true;

            await axios.put(`${constants.API_URL}/instructor/admin/private_services`, payload);

            if (acceptSeriesTerms) props.onSeriesTermsAccepted?.();
            props.refetch();
            alert?.showAlert(t('serviceUpdated'));
            setIsDialogOpen(false);
        } catch (error) {
            if (error instanceof AxiosError) {
                alert?.showAlert(getLocalizedErrorMessage(error, 'he') || t('somethingWentWrong'), 'error');
            }
            alert?.showAlert(t('somethingWentWrong'), 'error');
        }
    };

    const handleAcceptTerms = async () => {
        const data = pendingSubmitData;
        setIsTermsDialogOpen(false);
        setPendingSubmitData(null);
        if (data) await submitSave(data, true);
    };

    const handleRejectTerms = () => {
        setIsTermsDialogOpen(false);
        setPendingSubmitData(null);
    };
    return (
        props.service && (
            <>
                <Dialog
                    maxWidth={'md'}
                    fullWidth
                    PaperProps={{ style: { borderRadius: 20 } }}
                    open={isDialogOpen}
                    onClose={() => {
                        setIsDialogOpen(false);

                    }}>
                    <div className='p-8 pt-4' dir={dir}>
                        {(() => {
                            const originalTitleHe = props.service.title?.he;
                            const originalType = props.service.type;

                            const isDisabled =
                                originalTitleHe === 'שיחת הכוונה' ||
                                originalTitleHe === 'שיחת היכרות לבדיקת התאמה לפגישת טיפול' ||
                                originalTitleHe === 'שיחת ייעוץ' ||
                                originalType === 'SAFE_SPACE_TRIAL';
                            return (
                                <>
                                    <div>
                                        <h1 className='text-center font-medium text-lg'>{t('editService')}</h1>
                                    </div>
                                    <TextField
                                        label={t('titleHebrew')}
                                        variant='standard'
                                        fullWidth
                                        disabled={isDisabled}
                                        inputProps={{ maxLength: TITLE_LIMIT }}
                                        {...form.register('title.he', { required: true, disabled: isDisabled })}
                                        helperText={`${(form.watch('title.he') as string)?.length || 0}/${TITLE_LIMIT}`}
                                    />
                                    <TextField
                                        label={t('titleEnglishService')}
                                        variant='standard'
                                        fullWidth
                                        disabled={isDisabled}
                                        inputProps={{ maxLength: TITLE_LIMIT }}
                                        {...form.register('title.en', { required: true, disabled: isDisabled })}
                                        helperText={`${(form.watch('title.en') as string)?.length || 0}/${TITLE_LIMIT}`}
                                    />
                                    <TextField
                                        label={t('descriptionHebrew')}
                                        helperText={`${(form.watch('description.he') as string)?.length || 0}/${DESCRIPTION_LIMIT}`}
                                        variant='standard'
                                        fullWidth
                                        {...form.register('description.he', { maxLength: DESCRIPTION_LIMIT })}
                                        multiline
                                        inputProps={{ maxLength: DESCRIPTION_LIMIT }}
                                    />
                                    <TextField
                                        label={t('descriptionEnglishService')}
                                        helperText={`${(form.watch('description.en') as string)?.length || 0}/${DESCRIPTION_LIMIT}`}
                                        variant='standard'
                                        fullWidth
                                        InputProps={{}}
                                        {...form.register('description.en', { maxLength: DESCRIPTION_LIMIT })}
                                        multiline
                                        inputProps={{ maxLength: DESCRIPTION_LIMIT }}
                                    />
                                    <div className='flex gap-4 mt-8'>
                                        <TextField
                                            type='number'
                                            label={t('serviceDuration')}
                                            variant='standard'
                                            fullWidth
                                            disabled={isDisabled}
                                            {...form.register('duration', { valueAsNumber: true, disabled: isDisabled })}
                                            InputProps={{ endAdornment: t('minutes') }}
                                        />
                                        <TextField
                                            type='number'
                                            label={t('breakAfterSession')}
                                            variant='standard'
                                            fullWidth
                                            {...form.register('gap_duration', { valueAsNumber: true })}
                                            InputProps={{ endAdornment: <span className='text-xs opacity-50'>{t('minutes')}</span> }}
                                        />
                                        <TextField
                                            type='number'
                                            label={t('price')}
                                            variant='standard'
                                            fullWidth
                                            disabled={isDisabled}
                                            {...form.register('price', { required: true, valueAsNumber: true, disabled: isDisabled })}
                                            InputProps={{ endAdornment: '₪' }}
                                        />
                                        <TextField
                                            type='number'
                                            label={t('priceBeforeDiscount')}
                                            variant='standard'
                                            fullWidth
                                            disabled={isDisabled}
                                            {...form.register('price_before_discount', { valueAsNumber: true, disabled: isDisabled })}
                                            InputProps={{ endAdornment: '₪' }}
                                        />
                                    </div>
                                    {constants.ENABLE_SERIES_EDITING &&
                                        (originalType === '1TO1' || originalType === '1TO1_SERIES') && (
                                            <div className='mt-6'>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={!!form.watch('is_series')}
                                                            onChange={(e) =>
                                                                form.setValue('is_series', e.target.checked)
                                                            }
                                                        />
                                                    }
                                                    label={t('isSeries')}
                                                />
                                                {form.watch('is_series') && (
                                                    <TextField
                                                        type='number'
                                                        label={t('seriesSize')}
                                                        variant='standard'
                                                        fullWidth
                                                        {...form.register('series_size', {
                                                            valueAsNumber: true,
                                                            min: 2,
                                                        })}
                                                    />
                                                )}
                                            </div>
                                        )}
                                </>
                            );
                        })()}

                        <div className='mt-8 flex justify-between '>
                            <Button
                                variant='text'
                                color='error'
                                onClick={() => {
                                    form.reset();
                                    setIsDialogOpen(false);
                                }}>
                                {t('cancel')}
                            </Button>
                            <Button variant='contained' onClick={form.handleSubmit(handleSave)}>
                                {t('save')}
                            </Button>
                        </div>
                    </div>
                </Dialog>
                <Dialog
                    open={isTermsDialogOpen}
                    onClose={handleRejectTerms}
                    maxWidth='sm'
                    fullWidth
                    PaperProps={{ style: { borderRadius: 15 } }}
                >
                    <div dir='rtl'>
                        <DialogTitle sx={{ fontWeight: 600 }}>אישור תנאים — שירות "תהליך"</DialogTitle>
                        <DialogContent dividers>
                            <DialogContentText sx={{ color: 'text.primary', mb: 2 }}>
                                <strong>דיסקליימר להגדרת סדרה ("תהליך"):</strong>
                                <br />
                                מרגע שהפגישה הוגדרה כתהליך והמנוי השלים את התשלום המלא, יש להשלים
                                את כלל מספר הפגישות שנקבעו בתהליך.
                            </DialogContentText>
                            <DialogContentText sx={{ color: 'text.primary', mb: 2 }}>
                                <strong>דיסקליימר ביטולים:</strong>
                                <br />
                                אם לאחר אחד המפגשים המנוי או המומחה אינם מעוניינים להמשיך בתהליך,
                                ניתן לפנות לתמיכת GroupHug ולבקש החזר יחסי בהתאם למספר הפגישות שכבר
                                התקיימו.
                            </DialogContentText>
                            <DialogContentText sx={{ color: 'text.primary', mb: 2 }}>
                                <strong>דיסקליימר זמן:</strong>
                                <br />
                                לאחר הזמנת שירות מסוג "תהליך", ניתן לקבוע את הפגישות לאורך תקופה של
                                עד ארבעה חודשים ממועד ההזמנה. בתום תקופה זו, האפשרות לקבוע פגישות
                                נוספות תיחסם, והמשך קביעת הפגישות יתאפשר רק בתיאום ובהסכמת המומחה.
                            </DialogContentText>
                            <DialogContentText sx={{ color: 'text.primary', mt: 2, fontWeight: 500 }}>
                                על מנת להוסיף סוג פגישה "תהליך", יש לאשר את תנאי השירות המפורטים
                                לעיל.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleRejectTerms} color='error'>
                                ביטול
                            </Button>
                            <Button onClick={handleAcceptTerms} variant='contained'>
                                אני מאשר/ת
                            </Button>
                        </DialogActions>
                    </div>
                </Dialog>
                <div
                    className={`w-full border-b py-2 px-4 flex flex-row justify-between transition-colors ${
                        props.service.wellness_session
                            ? 'bg-purple-50 border-s-4 border-s-purple-400 hover:bg-purple-100'
                            : 'hover:bg-gradient-to-l from-gray-100 to-white'
                    }`}
                >
                    <span>
                        <h1 className='font-semibold'>
                            {props.service.title?.he}
                            {props.service.wellness_session && (
                                <span className='ms-2 align-middle inline-block rounded-full bg-purple-600 text-white text-xs font-normal px-2 py-0.5'>
                                    {t('wellnessSessionsLabel')}
                                </span>
                            )}
                        </h1>
                        <span className='text-sm flex-row gap-4 flex'>
                            <span>{`${props.service.price} ₪`}</span>
                            <span className='border-s border-dotted border-black my-1' />
                            <span>{`${props.service.duration} ${t('minutes')}`}</span>
                            <span className='border-s border-dotted border-black my-1' />
                            <span>{props.service.public ? t('paidSession') : t('subscriberSession')}</span>
                        </span>
                    </span>

                    <span className='flex flex-row gap-2 items-center'>
                        {props.service.wellness_session ? (
                            <Tooltip title={t('managedByGroupHug')}>
                                <span className='flex flex-row gap-1 items-center text-gray-400'>
                                    <Lock fontSize='small' />
                                </span>
                            </Tooltip>
                        ) : (
                            <IconButton onClick={() => setIsDialogOpen(true)}>
                                <Edit />
                            </IconButton>
                        )}
                    </span>
                </div>
            </>
        )
    );
};
const PrivateServicesTable = ({
    privateServices = [],
    seriesTermsConfirmed: seriesTermsConfirmedProp,
    ...props
}: {
    privateServices?: IPrivateService[];
    refetch: () => void;
    seriesTermsConfirmed?: Date | string | null;
} & Partial<TableProps>) => {
    const [acceptedThisSession, setAcceptedThisSession] = useState(false);
    const seriesTermsConfirmed = !!seriesTermsConfirmedProp || acceptedThisSession;
    return (
        <div className='flex flex-col'>
            {privateServices?.map((service, index) => (
                <PrivateServiceItem
                    service={service}
                    key={`private-session-row#${index}`}
                    refetch={props.refetch}
                    seriesTermsConfirmed={seriesTermsConfirmed}
                    onSeriesTermsAccepted={() => setAcceptedThisSession(true)}
                />
            ))}
        </div>
    );
};

export default PrivateServicesTable;
